import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import HelpButton from "@/components/HelpButton/HelpButton";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { DeleteOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import DeleteConsumableAction from "../DeleteConsumableAction";
import { useConsumableStatusPerYear, useGetConsumables } from "../useConsumables";
import AddConsumable from "./AddConsumable";
import styles from "./ConsumableOverview.module.scss";

export interface Consumable {
  id: number;
  name: string;
  direct_impact_dataset: ImpactDataset | null;
  eol_impact_dataset: ImpactDataset | null;
  status: "complete" | "incomplete";
  deletable: boolean;
  facility_relations: number;
  product_relations: number;
}

export default function ProductsOverview() {
  useTrackOnLoad("Consumables Viewed");

  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const columns: Column<Consumable>[] = [
    {
      key: "name",
      label: "Name",
      maxWidth: "1fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
    },
    {
      key: "direct_name",
      label: "Impact",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
      renderer: (record) => record.direct_impact_dataset?.name || "-",
    },
    {
      key: "eol_name",
      label: "End-of-life impact",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
      renderer: (record) => record.eol_impact_dataset?.name || "-",
    },
    {
      key: "product_relations",
      label: "Product relations",
      maxWidth: "180px",
      minWidth: "146px",
      sortable: true,
    },
    {
      key: "facility_relations",
      label: "Facility relations",
      maxWidth: "180px",
      minWidth: "146px",
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "",
      minWidth: "50px",
      maxWidth: "50px",
      noLink: true,
      renderer: (consumable) => (
        <ActionsPopover
          actions={
            <>
              <DeleteConsumableAction consumable={consumable}>
                <>
                  <ActionsPopOverItem
                    icon={<DeleteOutlined />}
                    disabled={!consumable.deletable}
                    danger
                  >
                    Delete
                  </ActionsPopOverItem>
                </>
              </DeleteConsumableAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const [isAddModalOpen, setIsAddModelOpen] = useState(false);

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const { data, isLoading, isFetching } = useGetConsumables({
    filter,
    sort,
    search,
    pagination,
    year,
    showUnrelated,
  });

  const { data: yearsData, isLoading: yearsIsLoading } = useConsumableStatusPerYear();

  const resetTable = () => {
    setPage(1);
  };

  return (
    <div className={styles.ProductsOverview}>
      <PageHeader
        title="Consumables"
        description="Your consumable database built from items used in facility production and product usage. Create, manage, and assign impact references to each consumable here."
        right={
          <>
            <HelpButton url="https://help.root-sustainability.com/en/articles/11086644-consumables" />
          </>
        }
      />
      <TableHeader
        left={
          <>
            <YearSelector
              optionData={yearsData}
              isLoading={yearsIsLoading}
              disableYears={showUnrelated}
              onChange={resetTable}
              right={
                <Tooltip
                  placement="bottom"
                  title="Show consumables that are not related to a year through products or facilities"
                >
                  <>
                    <SwitchWithLabel
                      togglePosition="right"
                      size="small"
                      checked={showUnrelated}
                      onChange={(disabled) => {
                        setShowUnrelated(disabled);
                      }}
                    >
                      Year unrelated
                    </SwitchWithLabel>
                  </>
                </Tooltip>
              }
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => setIsAddModelOpen(true)}
            >
              Add
            </Button>
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<Consumable>
        availableFilters={{
          status: [
            { label: "Complete", value: "complete" },
            { label: "Incomplete", value: "incomplete" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(record) => `/consumables/${record.id}`}
      />
      <AddConsumable
        width={400}
        open={isAddModalOpen}
        afterClose={() => setIsAddModelOpen(false)}
        onCancel={() => setIsAddModelOpen(false)}
      />
    </div>
  );
}
